import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Plus, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../components/shared/hooks';
import { useExamTypes } from '../hooks/useExamTypes';
import {
  PatientForm, PatientDetail, PatientsTable, PatientsFilters,
  API_URL, ITEMS_PER_PAGE, initialFormData, formatCurrency, calculateTotal, formatDateDisplay
} from '../components/patients';
import { SaleModal } from '../components/sales';

const Patients = () => {
  const { hasRole, token } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dynamic exam types from API (full objects with prices)
  const { examTypes: dynamicExamTypesData, examTypeNames: dynamicExamTypes } = useExamTypes(token);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEnviado, setFilterEnviado] = useState('all');
  const [filterExamen, setFilterExamen] = useState('all');
  const [filterInstitucion, setFilterInstitucion] = useState('all');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({ ...initialFormData, fecha: format(new Date(), 'yyyy-MM-dd') });
  const [linkedAppointmentId, setLinkedAppointmentId] = useState(null);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);
  
  // Sale modal
  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false);
  const [patientForSale, setPatientForSale] = useState(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPatients();
    
    // Check for institution filter from URL params
    const institucionParam = searchParams.get('institucion');
    if (institucionParam) {
      setFilterInstitucion(institucionParam);
    }
  }, [searchParams]);

  // Handle data from appointment
  useEffect(() => {
    if (location.state?.fromAppointment && location.state?.appointmentData) {
      const appointmentData = location.state.appointmentData;
      setEditingPatient(null);
      setLinkedAppointmentId(appointmentData.appointmentId || null);
      
      // Build examenes from appointment data
      let examenesFromAppointment = [];
      
      if (appointmentData.examenes && appointmentData.examenes.length > 0) {
        // Use examenes from appointment with prices from config
        examenesFromAppointment = appointmentData.examenes.map(exam => {
          const matchedExamType = dynamicExamTypesData.find(
            et => et.name.toLowerCase() === (exam.tipo_examen || '').toLowerCase()
          );
          const defaultPrice = matchedExamType?.default_price || 0;
          return {
            tipo_examen: exam.tipo_examen || '',
            ojo_estudiar: '',
            precio: defaultPrice.toString(),
            duracion: exam.duracion || 30,
          };
        });
      } else if (appointmentData.tipo_examen) {
        // Fallback to old single tipo_examen format
        const matchedExamType = dynamicExamTypesData.find(
          et => et.name.toLowerCase() === (appointmentData.tipo_examen || '').toLowerCase()
        );
        const defaultPrice = matchedExamType?.default_price || 0;
        examenesFromAppointment = [{
          tipo_examen: appointmentData.tipo_examen || '',
          ojo_estudiar: '',
          precio: defaultPrice.toString(),
          duracion: appointmentData.duracion || 30,
        }];
      } else {
        examenesFromAppointment = [{ tipo_examen: '', ojo_estudiar: '', precio: '0', duracion: 30 }];
      }
      
      setFormData({
        ...initialFormData,
        fecha: appointmentData.fecha || format(new Date(), 'yyyy-MM-dd'),
        nombre: appointmentData.nombre || '',
        fecha_nacimiento: appointmentData.fecha_nacimiento || '',
        edad: appointmentData.edad?.toString() || '',
        telefono: appointmentData.telefono || '',
        correo: appointmentData.correo || '',
        nit: appointmentData.nit || '',
        medico_referente: appointmentData.medico_referente || '',
        institucion: appointmentData.institucion || '',
        // IGSS fields
        igss_afiliacion: appointmentData.igss_afiliacion || '',
        igss_codigo: appointmentData.igss_codigo || '',
        igss_nog: appointmentData.igss_nog || '',
        igss_spg: appointmentData.igss_spg || '',
        igss_unidad_referente: appointmentData.igss_unidad_referente || '',
        examenes: examenesFromAppointment,
      });
      setIsModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, dynamicExamTypesData]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patients`, { headers });
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      const matchesSearch = !debouncedSearchTerm || 
        patient.nombre.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        patient.telefono.includes(debouncedSearchTerm) ||
        (patient.nit && patient.nit.includes(debouncedSearchTerm));
      
      const matchesEnviado = filterEnviado === 'all' || 
        (filterEnviado === 'true' ? patient.enviado : !patient.enviado);
      
      const matchesExamen = filterExamen === 'all' || 
        patient.tipo_examen.toLowerCase().includes(filterExamen.toLowerCase());
      
      const matchesInstitucion = filterInstitucion === 'all' || 
        patient.institucion === filterInstitucion;
      
      let matchesDate = true;
      if (filterDateStart) matchesDate = matchesDate && patient.fecha >= filterDateStart;
      if (filterDateEnd) matchesDate = matchesDate && patient.fecha <= filterDateEnd;
      
      return matchesSearch && matchesEnviado && matchesExamen && matchesInstitucion && matchesDate;
    });
  }, [patients, debouncedSearchTerm, filterEnviado, filterExamen, filterInstitucion, filterDateStart, filterDateEnd]);

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / ITEMS_PER_PAGE);
  const paginatedPatients = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPatients.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredPatients, currentPage]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterEnviado, filterExamen, filterInstitucion, filterDateStart, filterDateEnd]);

  // Get unique exam types
  const examTypes = useMemo(() => {
    const types = [...new Set(patients.map(p => p.tipo_examen).filter(Boolean))];
    return types.sort();
  }, [patients]);

  // Get unique institutions
  const institutions = useMemo(() => {
    const insts = [...new Set(patients.map(p => p.institucion).filter(Boolean))];
    return insts.sort();
  }, [patients]);

  const hasActiveFilters = searchTerm || filterEnviado !== 'all' || filterExamen !== 'all' || filterInstitucion !== 'all' || filterDateStart || filterDateEnd;

  const clearFilters = () => {
    setSearchTerm('');
    setFilterEnviado('all');
    setFilterExamen('all');
    setFilterInstitucion('all');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  // Modal handlers
  const openCreateModal = () => {
    setEditingPatient(null);
    setLinkedAppointmentId(null);
    setFormData({ 
      ...initialFormData, 
      fecha: format(new Date(), 'yyyy-MM-dd'),
      examenes: [{ tipo_examen: '', ojo_estudiar: '', precio: '' }]
    });
    setIsModalOpen(true);
  };

  const openEditModal = (patient) => {
    setEditingPatient(patient);
    // Handle migration from legacy single exam to examenes array
    let examenes = patient.examenes || [];
    if (examenes.length === 0 && patient.tipo_examen) {
      examenes = [{
        tipo_examen: patient.tipo_examen || '',
        ojo_estudiar: patient.ojo_estudiar || '',
        precio: patient.precio?.toString() || ''
      }];
    }
    if (examenes.length === 0) {
      examenes = [{ tipo_examen: '', ojo_estudiar: '', precio: '' }];
    }
    
    setFormData({
      fecha: patient.fecha || '',
      nombre: patient.nombre || '',
      fecha_nacimiento: patient.fecha_nacimiento || '',
      edad: patient.edad?.toString() || '',
      telefono: patient.telefono || '',
      correo: patient.correo || '',
      nit: patient.nit || '',
      institucion: patient.institucion || '',
      igss_afiliacion: patient.igss_afiliacion || '',
      igss_codigo: patient.igss_codigo || '',
      igss_nog: patient.igss_nog || '',
      igss_spg: patient.igss_spg || '',
      igss_unidad_referente: patient.igss_unidad_referente || '',
      ojo_estudiar: patient.ojo_estudiar || '',
      tipo_examen: patient.tipo_examen || '',
      medico_referente: patient.medico_referente || '',
      precio: patient.precio?.toString() || '',
      examenes: examenes,
      tipo_descuento: patient.tipo_descuento || 'sin_descuento',
      descuento: patient.descuento?.toString() || '0',
      medico_autoriza_descuento: patient.medico_autoriza_descuento || '',
      forma_pago: patient.forma_pago || 'Efectivo',
      no_autorizacion: patient.no_autorizacion || '',
      observaciones: patient.observaciones || '',
      enviado: patient.enviado || false,
    });
    setIsModalOpen(true);
  };

  const openDetailModal = (patient) => {
    setSelectedPatient(patient);
    setIsDetailModalOpen(true);
  };

  // Save patient
  const handleSavePatient = async (e) => {
    e.preventDefault();
    
    // Validate examenes
    const examenes = formData.examenes || [];
    const validExamenes = examenes.filter(e => e.tipo_examen && e.precio);
    
    if (validExamenes.length === 0) {
      toast.error('Agregue al menos un examen con tipo y precio');
      return;
    }
    
    if (!formData.nombre || !formData.edad || !formData.telefono) {
      toast.error('Complete los campos requeridos');
      return;
    }

    // Calculate total price from examenes
    const totalPrecio = validExamenes.reduce((sum, e) => sum + (parseFloat(e.precio) || 0), 0);
    const firstExam = validExamenes[0];
    
    // Si es Cortesía, el precio total es 0
    const finalPrecio = formData.forma_pago === 'Cortesía' ? 0 : totalPrecio;

    const payload = {
      ...formData,
      edad: parseInt(formData.edad) || 0,
      // Legacy fields for backwards compatibility
      tipo_examen: validExamenes.map(e => e.tipo_examen).join(', '),
      ojo_estudiar: firstExam.ojo_estudiar || '',
      precio: finalPrecio,
      // New examenes array
      examenes: validExamenes.map(e => ({
        tipo_examen: e.tipo_examen,
        ojo_estudiar: e.ojo_estudiar || '',
        precio: formData.forma_pago === 'Cortesía' ? 0 : (parseFloat(e.precio) || 0)
      })),
      descuento: parseFloat(formData.descuento) || 0,
    };

    setSaving(true);
    try {
      if (editingPatient) {
        await axios.put(`${API_URL}/api/patients/${editingPatient.id}`, payload, { headers });
        toast.success('Paciente actualizado');
      } else {
        await axios.post(`${API_URL}/api/patients`, payload, { headers });
        toast.success('Paciente registrado');
        
        // If from appointment, mark appointment as completed
        if (linkedAppointmentId) {
          try {
            await axios.put(`${API_URL}/api/appointments/${linkedAppointmentId}/status?status=COMPLETADA`, {}, { headers });
          } catch (err) {
            console.error('Error updating appointment:', err);
          }
        }
      }
      setIsModalOpen(false);
      fetchPatients();
    } catch (error) {
      console.error('Error saving patient:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete patient
  const handleDeletePatient = async () => {
    if (!patientToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/patients/${patientToDelete.id}`, { headers });
      toast.success('Paciente eliminado');
      setDeleteDialogOpen(false);
      setPatientToDelete(null);
      fetchPatients();
    } catch (error) {
      console.error('Error deleting patient:', error);
      toast.error('Error al eliminar');
    }
  };

  // Toggle enviado status (Estudios)
  const handleToggleEnviado = async (patient) => {
    try {
      await axios.put(`${API_URL}/api/patients/${patient.id}`, 
        { enviado: !patient.enviado }, 
        { headers }
      );
      toast.success(patient.enviado ? 'Estudios: Marcado como pendiente' : 'Estudios: Marcado como enviado');
      fetchPatients();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar estado de estudios');
    }
  };

  // Toggle informe_enviado status (Informe)
  const handleToggleInformeEnviado = async (patient) => {
    try {
      await axios.put(`${API_URL}/api/patients/${patient.id}`, 
        { informe_enviado: !patient.informe_enviado }, 
        { headers }
      );
      toast.success(patient.informe_enviado ? 'Informe: Marcado como pendiente' : 'Informe: Marcado como enviado');
      fetchPatients();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar estado de informe');
    }
  };

  // Export functions
  const exportToExcel = () => {
    const exportData = filteredPatients.map(p => ({
      Fecha: formatDateDisplay(p.fecha),
      Nombre: p.nombre,
      Edad: p.edad,
      Telefono: p.telefono,
      Correo: p.correo || '',
      NIT: p.nit || '',
      Institucion: p.institucion || '',
      'Tipo Examen': p.tipo_examen,
      'Medico Referente': p.medico_referente || '',
      Precio: p.precio,
      Descuento: p.descuento || 0,
      Total: calculateTotal(p.precio, p.tipo_descuento, p.descuento),
      'Forma Pago': p.forma_pago,
      'Estudios Enviados': p.enviado ? 'Sí' : 'No',
      'Informe Enviado': p.informe_enviado ? 'Sí' : 'No'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pacientes');
    XLSX.writeFile(wb, `Pacientes_${format(new Date(), 'dd-MM-yy')}.xlsx`);
    toast.success('Exportado a Excel');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.setTextColor(26, 81, 146);
    doc.text('Oftalmodiagnosis', 14, 20);
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Listado de Pacientes', 14, 28);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generado: ${format(new Date(), 'dd/MM/yy HH:mm')}`, 14, 35);

    const tableData = filteredPatients.map(p => [
      formatDateDisplay(p.fecha),
      p.nombre,
      p.edad,
      p.telefono,
      p.tipo_examen,
      formatCurrency(calculateTotal(p.precio, p.tipo_descuento, p.descuento)),
      p.enviado ? 'Sí' : 'No',
      p.informe_enviado ? 'Sí' : 'No'
    ]);

    autoTable(doc, {
      startY: 42,
      head: [['Fecha', 'Nombre', 'Edad', 'Teléfono', 'Examen', 'Total', 'Estudios', 'Informe']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [26, 81, 146], fontSize: 8 },
      styles: { fontSize: 7, cellPadding: 2 },
    });

    doc.save(`Pacientes_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('Exportado a PDF');
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-[#1a5192]" />
            Base de Datos de Pacientes
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {filteredPatients.length} pacientes encontrados
          </p>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-[#1a5192] hover:bg-[#0d3a6e] text-white font-semibold shadow-lg"
          data-testid="new-patient-button"
        >
          <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
          Nuevo Paciente
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-100 shadow-soft">
        <CardContent className="p-4">
          <PatientsFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterEnviado={filterEnviado}
            setFilterEnviado={setFilterEnviado}
            filterExamen={filterExamen}
            setFilterExamen={setFilterExamen}
            filterInstitucion={filterInstitucion}
            setFilterInstitucion={setFilterInstitucion}
            filterDateStart={filterDateStart}
            setFilterDateStart={setFilterDateStart}
            filterDateEnd={filterDateEnd}
            setFilterDateEnd={setFilterDateEnd}
            examTypes={examTypes}
            institutions={institutions}
            onClearFilters={clearFilters}
            onExportExcel={exportToExcel}
            onExportPDF={exportToPDF}
            hasActiveFilters={hasActiveFilters}
          />
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-slate-100 shadow-soft">
        <CardContent className="p-0">
          <PatientsTable
            patients={paginatedPatients}
            loading={loading}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onView={openDetailModal}
            onEdit={openEditModal}
            onDelete={(patient) => { setPatientToDelete(patient); setDeleteDialogOpen(true); }}
            onToggleEnviado={handleToggleEnviado}
            onToggleInformeEnviado={handleToggleInformeEnviado}
            onGenerateSale={(patient) => { setPatientForSale(patient); setIsSaleModalOpen(true); }}
            hasRole={hasRole}
          />
        </CardContent>
      </Card>

      {/* Patient Form Modal */}
      <PatientForm
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingPatient={editingPatient}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSavePatient}
        saving={saving}
        token={token}
        linkedAppointmentId={linkedAppointmentId}
        examTypeNames={dynamicExamTypes}
        examTypesData={dynamicExamTypesData}
      />

      {/* Patient Detail Modal */}
      <PatientDetail
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        patient={selectedPatient}
        onEdit={() => {
          setIsDetailModalOpen(false);
          openEditModal(selectedPatient);
        }}
        hasRole={hasRole}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este paciente?</AlertDialogTitle>
            <AlertDialogDescription>
              {patientToDelete && (
                <>
                  Está a punto de eliminar el registro de "<strong>{patientToDelete.nombre}</strong>".
                  <br /><br />
                  Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePatient}
              className="bg-red-500 hover:bg-red-600"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sale Modal */}
      <SaleModal
        isOpen={isSaleModalOpen}
        onClose={() => { setIsSaleModalOpen(false); setPatientForSale(null); }}
        patient={patientForSale}
        token={token}
        onSaleCreated={() => {
          // Optionally refresh or navigate to sales
        }}
      />
    </div>
  );
};

export default Patients;
